import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface IllustrationProps {
  width: number;
  height: number;
  className?: string;
  children: ReactNode;
}

interface PathProps {
  d: string;
}

interface OutlineProps extends PathProps {
  dashed?: boolean;
}

// One colour at fixed opacities over the page background follows the theme,
// which an <img> with set colours can't do. By default one viewBox unit is one
// pixel, and a w-* class scales that. Text always sits next to an illustration,
// so screen readers skip it.
export function Illustration({
  width,
  height,
  className,
  children,
}: IllustrationProps) {
  return (
    <svg
      aria-hidden
      width={width}
      viewBox={`0 0 ${width} ${height}`}
      fill="currentColor"
      className={cn("max-w-full flex-none text-primary", className)}
    >
      {children}
    </svg>
  );
}

// Paints the page background first, so nothing behind shows through the tints.
export function PageFill({ d }: PathProps) {
  return <path className="fill-background" d={d} />;
}

export function FaintFill({ d }: PathProps) {
  return <path fillOpacity={0.06} d={d} />;
}

export function StrongFill({ d }: PathProps) {
  return <path fillOpacity={0.22} d={d} />;
}

// The line stays 1.75 px at every size.
export function Outline({ d, dashed }: OutlineProps) {
  return (
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={dashed ? "5 5" : undefined}
      vectorEffect="non-scaling-stroke"
      d={d}
    />
  );
}
